import {createAsyncThunk} from "@reduxjs/toolkit";
import {iStateMessage} from "@/customTypes/GenericReduxTypes";

import {iGenericResponse} from "@/customTypes/CommonServiceTypes";
import {curryGetThunkName} from "@/projectUtils/ReduxUtil";
import {httpStatusCodes} from "@/customTypes/NetworkTypes";
import { pluginService } from "@/services/api/cresivoxServer";

// import { REDUCER_NAME, iLogoDTO } from './Types';
import { REDUCER_NAME , iPluginDTO } from "./Types";
const curriedGetThunkName = curryGetThunkName(REDUCER_NAME);


export const addPlugin = createAsyncThunk<
  iGenericResponse<any | null>,
  { [key: string]: boolean },
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("addPlugin"),
  // @ts-expect-error
  async (body, { rejectWithValue }) => {
    try {
      const response = await pluginService.addPlugin(body);

      if (response) {
        const { httpStatusCode, data, message } = response;

        if (httpStatusCode === httpStatusCodes.SUCCESS_OK && data?.data) {
          return {
            data: data.data,
            message,
          };
        }

        return rejectWithValue({
          httpStatusCode,
          message,
        });
      }

      return rejectWithValue({
        message: "No response received",
      });
    } catch (error) {
      console.error("🚀 ~ error:", error);
      return rejectWithValue({
        message: "Something went wrong",
      });
    }
  }
);


export const addGoogleAnalytics = createAsyncThunk<
  iGenericResponse<any | null>,
  { [key: string]: boolean },
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("addGoogleAnalytics"),
  // @ts-expect-error
  async (body, { rejectWithValue }) => {
    try {
      const response = await pluginService.addGoogleAnalytics(body);

      if (response) {
        const { httpStatusCode, data, message } = response;

        if (httpStatusCode === httpStatusCodes.SUCCESS_OK && data?.data) {
          return {
            data: data.data,
            message,
          };
        }

        return rejectWithValue({
          httpStatusCode,
          message,
        });
      }

      return rejectWithValue({
        message: "No response received",
      });
    } catch (error) {
      console.error("🚀 ~ error:", error);
      return rejectWithValue({
        message: "Something went wrong",
      });
    }
  }
);

export const addOpenAi = createAsyncThunk<
  iGenericResponse<any | null>,
  { [key: string]: boolean },
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("addOpenAi"),
  // @ts-expect-error
  async (body, { rejectWithValue }) => {
    try {
      const response = await pluginService.addOpenAi(body);

      if (response) {
        const { httpStatusCode, data, message } = response;

        if (httpStatusCode === httpStatusCodes.SUCCESS_OK && data?.data) {
          return {
            data: data.data,
            message,
          };
        }

        return rejectWithValue({
          httpStatusCode,
          message,
        });
      }

      return rejectWithValue({
        message: "No response received",
      });
    } catch (error) {
      console.error("🚀 ~ error:", error);
      return rejectWithValue({
        message: "Something went wrong",
      });
    }
  }
);


export const getPlugin = createAsyncThunk<
  iGenericResponse<any | null> | null,
  void,
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName("getPlugin"),
  // @ts-expect-error: Type mismatch between LoginSchema and userLogin expected types
  async (arg, {rejectWithValue}) => {
    try {
      const response = await pluginService.getPlugin();
      if (response) {
        const {httpStatusCode, data, message} = response;
        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_OK: {
            if (data && data.data) {
              return {
                data: {
                  httpStatusCode,
                  data: data.data,
                  message,
                }
              };
            } else {
              return rejectWithValue({
                httpStatusCode,
                message,
              });
            }
          }
          default: {
            return rejectWithValue({
              httpStatusCode,
              message,
            });
          }
        }
      } else {
        return rejectWithValue({
          message: "No response received",
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: "Something went wrong",
      });
    }
  },
);


export const getGoogleAnalytics = createAsyncThunk<
  any,
  void,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("getGoogleAnalytics"),
  async (_, { rejectWithValue }) => {
    try {
      const response = await pluginService.getGoogleAnalytics();

      if (!response) {
        return rejectWithValue({ message: "No response received" });
      }

      const { httpStatusCode, data, message } = response;

      if (httpStatusCode !== httpStatusCodes.SUCCESS_OK) {
        return rejectWithValue({ httpStatusCode, message });
      }

      // 🎯 extract real config
      const config = data?.config;

      if (!config) {
        return rejectWithValue({
          httpStatusCode,
          message: "Google analytics not configured",
        });
      }

      // return only usable data
      return config;

    } catch (error) {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);


export const getOpenAi = createAsyncThunk<
  { vendorId: string; apiKey: string },
  void,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("getOpenAiKey"),
  async (_, { rejectWithValue }) => {
    try {
      const response = await pluginService.getOpenAi();

      if (!response) {
        return rejectWithValue({ message: "No response received" });
      }

      const { httpStatusCode, data, message } = response;

      if (httpStatusCode !== httpStatusCodes.SUCCESS_OK) {
        return rejectWithValue({ httpStatusCode, message });
      }

      // ✅ correct extraction based on your backend response
      const apiData = data?.data;

      if (!apiData) {
        return rejectWithValue({
          httpStatusCode,
          message: "OpenAI API key not configured",
        });
      }

      return apiData;

    } catch (error) {

      return rejectWithValue({
        message: "Something went wrong while fetching OpenAI key",
      });
    }
  }
);

// getGooglePixel

export const getGooglePixel = createAsyncThunk<
  any[],
  void,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("getGooglePixel"),
  async (_, { rejectWithValue }) => {
    try {
      const response = await pluginService.getGooglePixel();
      if (!response) {
        return rejectWithValue({ message: "No response received" });
      }

      const { httpStatusCode, data, message } = response;

      if (httpStatusCode !== httpStatusCodes.SUCCESS_OK) {
        return rejectWithValue({ httpStatusCode, message });
      }
      const analyticsArray = data?.data ?? [];

      return analyticsArray;

    } catch (error) {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

// getAnalyticsDashboard — feeds the /analytics page (see googleSearchRoute's
// GET /ga4/analytics-dashboard). Payload includes `connected: false` when
// the vendor has no GA4 config yet — that's a normal response, not a
// rejection, so the widget layer can show a "Connect Google Analytics"
// prompt instead of fake data.
export const getAnalyticsDashboard = createAsyncThunk<
  any,
  void,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("getAnalyticsDashboard"),
  async (_, { rejectWithValue }) => {
    try {
      const response = await pluginService.getAnalyticsDashboard();
      if (!response) {
        return rejectWithValue({ message: "No response received" });
      }

      const { httpStatusCode, data, message } = response;

      if (httpStatusCode !== httpStatusCodes.SUCCESS_OK) {
        return rejectWithValue({ httpStatusCode, message });
      }

      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);




// export const addWhatsAppPlugin = createAsyncThunk<
export const addWhatsAppPlugin = createAsyncThunk<
  iGenericResponse<any | null>,
  Record<string, any>,
  { rejectValue: iStateMessage }
>(
  "plugin/addWhatsAppPlugin",
  // @ts-expect-error
 async (body, { rejectWithValue }) => {
  try {
    const response = await pluginService.addWhatsAppPlugin(body);

    

    // If HTTP is 200 → always return success
   if (response?.httpStatusCode === 200) {
  const apiMessage = response?.data?.data?.message;

  return {
    data: response.data,
    message: apiMessage || "Plugin Added Successfully",
  };
}

    return rejectWithValue({
      message: response?.message ?? "Something went wrong",
    });

  } catch (err: any) {
    return rejectWithValue({
      message: err?.message ?? "Something went wrong",
    });
  }
}
);

export const getWhatsAppPlugin = createAsyncThunk<
  iGenericResponse<any | null> | null,
  void,
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName("getWhatsAppPlugin"),
  // @ts-expect-error: Type mismatch between LoginSchema and userLogin expected types
  async (arg, {rejectWithValue}) => {
    try {
      const response = await pluginService.getWhatsAppPlugin();
      if (response) {
        const {httpStatusCode, data, message} = response;
        
        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_OK: {
            if (data && data.data) {
              return {
                data: {
                  httpStatusCode,
                  data: data.data,
                  message,
                }
              };
            } else {
              return rejectWithValue({
                httpStatusCode,
                message,
              });
            }
          }
          default: {
            return rejectWithValue({
              httpStatusCode,
              message,
            });
          }
        }
      } else {
        return rejectWithValue({
          message: "No response received",
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: "Something went wrong",
      });
    }
  },
);

//addTwilioPlugin satup

 export const addTwilioPlugin = createAsyncThunk<
  iGenericResponse<any | null>,
  Record<string, any>, // Accepts any form key/value
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("addTwilioPlugin"),
  // @ts-expect-error
  async (body, { rejectWithValue }) => {
    try {
      const response = await pluginService.addTwilioPlugin(body); // ✅ pass form data here

      if (response) {
        const { httpStatusCode, data, message } = response;

        if (httpStatusCode === httpStatusCodes.SUCCESS_OK && data?.data) {
          return {
            data: data.data,
            message,
          };
        }

        return rejectWithValue({
          httpStatusCode,
          message,
        });
      }

      return rejectWithValue({
        message: "No response received",
      });
    } catch (error) {
      console.error("🚀 ~ error:", error);
      return rejectWithValue({
        message: "Something went wrong",
      });
    }
  }
);


export const AddGooglePlugIn = createAsyncThunk<
  iGenericResponse<any | null>,
  Record<string, any>, // Accepts any form key/value
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("AddGooglePlugIn"),
  // @ts-expect-error
  async (body, { rejectWithValue }) => {
    try {
      const response = await pluginService.AddGooglePlugIn(body); // ✅ pass form data here

      if (response) {
        const { httpStatusCode, data, message } = response;

        if (httpStatusCode === httpStatusCodes.SUCCESS_OK && data?.data) {
          return {
            data: data.data,
            message,
          };
        }

        return rejectWithValue({
          httpStatusCode,
          message,
        });
      }

      return rejectWithValue({
        message: "No response received",
      });
    } catch (error) {
      console.error("🚀 ~ error:", error);
      return rejectWithValue({
        message: "Something went wrong",
      });
    }
  }
);


export const GetGooglePlugIn = createAsyncThunk<
  iGenericResponse<any | null> | null,
  void,
  {
    rejectValue: iStateMessage;
  }
>(
  curriedGetThunkName("GetGooglePlugIn"),
  // @ts-expect-error: Type mismatch between LoginSchema and userLogin expected types
  async (arg, {rejectWithValue}) => {
    try {
      const response = await pluginService.GetGooglePlugIn();
      if (response) {
        const {httpStatusCode, data, message} = response;
        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_OK: {
           if (data?.config) {
  return {
    data: data.config, // ✅ correct path
    message,
  };
}
             else {
              return rejectWithValue({
                httpStatusCode,
                message,
              });
            }
          }
          default: {
            return rejectWithValue({
              httpStatusCode,
              message,
            });
          }
        }
      } else {
        return rejectWithValue({
          message: "No response received",
        });
      }
    } catch (error) {
      return rejectWithValue({
        message: "Something went wrong",
      });
    }
  },
);

export const getCRMStatus = createAsyncThunk<
  iGenericResponse<any | null> | null,
  void,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName('getCRMStatus'),
  async (_, { rejectWithValue }) => {
    try {
      const response = await pluginService.getCRMStatus();
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
          return { data: { httpStatusCode, data: data?.data, message } };
        }
        return rejectWithValue({ httpStatusCode, message });
      }
      return rejectWithValue({ message: 'No response received' });
    } catch {
      return rejectWithValue({ message: 'Something went wrong' });
    }
  }
);

export const purchaseCRM = createAsyncThunk<
  iGenericResponse<any | null> | null,
  number,
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName('purchaseCRM'),
  async (months, { rejectWithValue }) => {
    try {
      const response = await pluginService.purchaseCRM(months);
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_OK) {
          return { data: { httpStatusCode, data: data?.data, message } };
        }
        return rejectWithValue({ httpStatusCode, message: (data as any)?.message || message });
      }
      return rejectWithValue({ message: 'No response received' });
    } catch {
      return rejectWithValue({ message: 'Something went wrong' });
    }
  }
);

// export const addTwoFactorPlugin = createAsyncThunk<
 export const addTwoFactorPlugin = createAsyncThunk<
  iGenericResponse<any | null>,
  Record<string, any>, // Accepts any form key/value
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("addTwoFactorPlugin"),
  // @ts-expect-error
  async (body, { rejectWithValue }) => {
    try {
      const response = await pluginService.addTwoFactorPlugin(body); // ✅ pass form data here

      if (response) {
        const { httpStatusCode, data, message } = response;

        if (httpStatusCode === httpStatusCodes.SUCCESS_OK && data?.data) {
          return {
            data: data.data,
            message,
          };
        }

        return rejectWithValue({
          httpStatusCode,
          message,
        });
      }

      return rejectWithValue({
        message: "No response received",
      });
    } catch (error) {
      console.error("🚀 ~ error:", error);
      return rejectWithValue({
        message: "Something went wrong",
      });
    }
  }
);